import CartIcon from "../../../assets/svgs/header/CartIcon";
import HeartIcon from "../../../assets/svgs/header/HeartIcon";
import SuccessIcon from "../../../assets/svgs/other/SuccessIcon";
import Button from "../button/Button";
import styles from "./ProductCard.module.scss";
import { useCurrencies } from "../../../context/CurrenciesContext";
import ProductsService from "../../../api/services/products.service";
import { useState } from "react";
import { useAuth } from "../../../context/AuthContext";
import Modal from "../modal/Modal";
import { useBasketWishlist } from "../../../context/BasketWishlistContext";
import { toast } from "sonner";
import FilledHeartIcon from "../../../assets/svgs/header/FilledHeartIcon";
import LocalizedLink from "../localized-link/LocalizedLink";
import { useLocalizedNavigate } from "../../../hooks/useLocalizedNavigate";
import { useTranslation } from "react-i18next";

interface Props {
  id: number;
  image: string;
  title: string;
  price: string;
  discount_price: string;
  discount_percent: string;
  currency: string;
  stock_quantity: number;
  wishlist: boolean;
  onRemove?: () => void;
  images: {
    id: number;
    image: string;
    order: number;
  }[];
  attributes: {
    attribute_id: number;
    attribute_name: string;
    attribute_type: string;
    id: number;
    value: string;
  }[];
}

const ProductCard = ({
  id,
  image,
  title,
  price,
  discount_price,
  discount_percent,
  stock_quantity,
  currency: currencyProp,
  wishlist,
  images,
  attributes,
  onRemove,
}: Props) => {
  const { currencyId } = useCurrencies();
  const { isLoggedIn } = useAuth();
  const { increaseBasket, increaseWishlist, decreaseWishlist } =
    useBasketWishlist();
  const { currency } = useCurrencies();
  const [addLoading, setAddLoading] = useState(false);
  const [addedSuccess, setAddedSuccess] = useState(false);
  const [isWishlist, setIsWishlist] = useState(wishlist);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [wishlistLoading, setWishlistLoading] = useState(false);
  const navigate = useLocalizedNavigate();
  const { t } = useTranslation("translation");

  const addToBasket = async () => {
    if (!isLoggedIn) {
      if (stock_quantity === 0) {
        toast.error(t("product_out_of_stock"));
        return;
      }

      const storedBasket = localStorage.getItem("guest_basket");
      let basketData = storedBasket ? JSON.parse(storedBasket) : [];
      const existingItemIndex = basketData.findIndex(
        (item: any) => item.product_variant_id === id,
      );
      if (existingItemIndex !== -1) {
        basketData[existingItemIndex].quantity += 1;
      } else {
        basketData.push({
          product_variant_id: id,
          quantity: 1,
          product: {
            id,
            images: images,
            name_en: title,
            price: parseFloat(price),
            discount_price: discount_price
              ? parseFloat(discount_price)
              : undefined,
            discount_percent: discount_percent
              ? parseFloat(discount_percent)
              : undefined,
            currency_name: currency || currencyProp,
            stock_quantity,
            attributes: attributes,
          },
        });
      }
      localStorage.setItem("guest_basket", JSON.stringify(basketData));
      increaseBasket(1);
      setAddedSuccess(true);
      setTimeout(() => setAddedSuccess(false), 1500);
      return;
    }
    setAddLoading(true);
    try {
      const formData = new FormData();
      formData.append("product_variant_id", id?.toString());
      formData.append("quantity", "1");
      await ProductsService.updateBasketAuth(Number(currencyId), formData);
      increaseBasket(1);
      setAddedSuccess(true);
      setTimeout(() => setAddedSuccess(false), 1500);
    } catch (error: any) {
      const rawMessage =
        error?.response?.data?.message || error?.response?.data?.error || "";

      if (
        typeof rawMessage === "string" &&
        rawMessage.toLowerCase().includes("not enough stock")
      ) {
        toast.error(t("product_out_of_stock"));
      } else {
        toast.error(t("error_adding_product"));
      }
    } finally {
      setAddLoading(false);
    }
  };

  const addToWishlist = async () => {
    if (!isLoggedIn) {
      setShowLoginModal(true);
      return;
    }
    setWishlistLoading(true);
    try {
      const formData = new FormData();
      formData.append("product_variant_id", id?.toString());
      const res = await ProductsService.removeAddWishlist(formData);
      if (res.data.message.includes("Remove") && onRemove) {
        onRemove();
      }
      if (res.data.message.includes("Remove")) {
        setIsWishlist(false);
        decreaseWishlist(1);
      } else {
        setIsWishlist(true);
        increaseWishlist(1);
      }
    } catch (error: any) {
      console.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          t("common_error"),
      );
    } finally {
      setWishlistLoading(false);
    }
  };

  return (
    <>
      <div className={styles.card}>
        <div className={styles.image_container}>
          {image ? (
            <LocalizedLink to={`/product/${id}`} className={styles.image_wrap}>
              <img src={image} alt={title} />
            </LocalizedLink>
          ) : (
            <LocalizedLink
              to={`/product/${id}`}
              className={`${styles.image_wrap} ${styles.no_image_wrap}`}
            >
              <p>{":("}</p>
            </LocalizedLink>
          )}
          <button
            className={styles.wishlist}
            onClick={addToWishlist}
            disabled={wishlistLoading}
          >
            {wishlistLoading ? (
              <div className={styles.loading_spinner}></div>
            ) : isWishlist ? (
              <FilledHeartIcon />
            ) : (
              <HeartIcon />
            )}
          </button>
        </div>

        <div className={styles.info}>
          <div className={styles.price_section}>
            <div className={styles.price_row}>
              <p className={styles.price}>
                {discount_price && Number(discount_percent) > 0
                  ? Number(discount_price).toFixed(0)
                  : Number(price).toFixed(0)}{" "}
                {currency || currencyProp}
              </p>
              {discount_percent && Number(discount_percent) > 0 && (
                <span className={styles.discount_badge}>
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M7 17L17 7M7 7h10v10" />
                  </svg>
                  {discount_percent}%
                </span>
              )}
            </div>
            {discount_percent && Number(discount_percent) > 0 && (
              <p className={styles.old_price}>
                {Number(price).toFixed(0)} {currency || currencyProp}
              </p>
            )}
          </div>

          <LocalizedLink to={`/product/${id}`} className={styles.title}>
            {title}
          </LocalizedLink>

          {stock_quantity > 0 ? (
            <div className={styles.stock}>
              <SuccessIcon />
              <p>
                {stock_quantity} {t("in_stock")}
              </p>
            </div>
          ) : (
            <div className={styles.stock}>
              <p className={styles.no_stock}>{t("no_stock")}</p>
            </div>
          )}

          <div className={styles.rating}>
            <div className={styles.stars}>
              <span>⭐</span>
              <span>⭐</span>
              <span>⭐</span>
              <span>⭐</span>
              <span>⭐</span>
            </div>
            <span className={styles.review_count}>11 {t("reviews")}</span>
          </div>

          <Button
            className={`${styles.add_btn} ${
              addedSuccess ? styles.added_success : ""
            }`}
            onClick={addToBasket}
            loading={addLoading}
            disabled={addLoading}
          >
            {addedSuccess ? (
              <>
                <CartIcon />
                <p>{t("added")}</p>
              </>
            ) : (
              <>
                <CartIcon />
                <p>{t("add_to_cart")}</p>
              </>
            )}
          </Button>
        </div>
      </div>

      <Modal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        btn_text={t("login") || ""}
        onSubmit={() => navigate("/auth/login")}
      >
        <div className={styles.modal}>
          <p>{t("must_login_wishlist")}</p>
        </div>
      </Modal>
    </>
  );
};

export default ProductCard;
